import { useEffect, useRef, type ReactNode, type RefObject } from "react";

/**
 * The one floating panel of choices.
 *
 * An autocomplete listbox, a notification dropdown and an account menu are the
 * same object; keeping them as one recipe is what stops a change to the
 * service-area picker from silently restyling the notification bell.
 *
 * This component owns the panel only. The TRIGGER stays with the caller,
 * because a combobox's trigger is an input with `aria-expanded` and a menu's is
 * a button with `aria-haspopup` — different contracts that must not be blurred.
 *
 * For `role="menu"` only, the panel also owns arrow-key movement: it focuses
 * its first item on mount and moves among `[role="menuitem"]` on
 * ArrowDown/ArrowUp/Home/End. `listbox` and `none` panels keep their own key
 * handling (the ServiceAreaPicker combobox) and gain nothing here.
 */
export function Menu({
  id,
  labelledBy,
  align = "stretch",
  onDismiss,
  children,
  role = "menu",
  triggerRef,
}: {
  id?: string;
  labelledBy?: string;
  /** `end` anchors to the trailing edge (bell, account); `stretch` spans the
   *  trigger (combobox). */
  align?: "stretch" | "end";
  /** Called on Escape and on a click outside the panel. */
  onDismiss?: () => void;
  children: ReactNode;
  role?: "menu" | "listbox" | "none";
  /** The button that opens this menu, so Tab-out's blur check does not treat
   *  moving focus back onto the trigger (e.g. a re-render on close) as
   *  "left the menu". */
  triggerRef?: RefObject<HTMLElement>;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!onDismiss) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismissRef.current?.();
    };
    const onPointerDown = (event: MouseEvent) => {
      const panel = panelRef.current;
      if (!panel) return;
      const target = event.target;
      if (target instanceof Node && !panel.contains(target)) onDismissRef.current?.();
    };

    document.addEventListener("keydown", onKeyDown);
    // `mousedown` in the CAPTURE phase, so a trigger that toggles on click
    // still sees its own event: a bubble-phase listener would close the panel
    // first and the trigger would immediately reopen it.
    document.addEventListener("mousedown", onPointerDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown, true);
    };
  }, [onDismiss]);

  // Separate from the effect above: the APG menu pattern applies whether or
  // not the caller passed `onDismiss`, and only to `role="menu"` — a listbox
  // owns its own combobox key handling.
  useEffect(() => {
    if (role !== "menu") return undefined;
    const panel = panelRef.current;
    if (!panel) return undefined;

    panel.querySelector<HTMLElement>('[role="menuitem"]')?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Tab") {
        // Tab leaves the menu, so the menu closes — but we do not
        // preventDefault or move focus ourselves: Tab's own default is
        // already taking focus to the next or previous element in the page.
        onDismissRef.current?.();
        return;
      }
      const items = [...panel.querySelectorAll<HTMLElement>('[role="menuitem"]')];
      if (items.length === 0) return;
      const current = items.indexOf(document.activeElement as HTMLElement);
      let next: number;
      switch (event.key) {
        case "ArrowDown":
          next = current < 0 ? 0 : (current + 1) % items.length;
          break;
        case "ArrowUp":
          next = current < 0 ? items.length - 1 : (current - 1 + items.length) % items.length;
          break;
        case "Home":
          next = 0;
          break;
        case "End":
          next = items.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      items[next]?.focus();
    };

    panel.addEventListener("keydown", onKeyDown);
    return () => panel.removeEventListener("keydown", onKeyDown);
  }, [role]);

  return (
    <div
      ref={panelRef}
      id={id}
      className={`menu${align === "end" ? " menu--end" : ""}`}
      role={role === "none" ? undefined : role}
      aria-labelledby={labelledBy}
      onBlur={(event) => {
        if (role !== "menu") return;
        const panel = panelRef.current;
        const related = event.relatedTarget;
        // A blur that lands back on the trigger (e.g. Escape's own focus
        // restore) is not "left the menu" — only somewhere else is.
        if (
          panel &&
          !(related instanceof Node && (panel.contains(related) || related === triggerRef?.current))
        ) {
          onDismissRef.current?.();
        }
      }}
    >
      {children}
    </div>
  );
}

export function MenuItem({
  children,
  onClick,
  href,
  selected,
  id,
  role = "menuitem",
}: {
  children: ReactNode;
  onClick?: () => void;
  href?: string;
  selected?: boolean;
  id?: string;
  role?: "menuitem" | "option";
}) {
  if (href) {
    return (
      <a className="menu__item" href={href} role={role} id={id}>
        {children}
      </a>
    );
  }
  return (
    <button
      type="button"
      className="menu__item"
      role={role}
      id={id}
      aria-selected={role === "option" ? selected : undefined}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
